import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BackButton, FormField, FormSection, PrimaryButton, SecondaryButton } from 'components/forms';
import PageTitle from 'components/UI/PageTitle';
import useCurrentUser from 'hooks/useCurrentUser';
import useIsAdmin from 'hooks/useIsAdmin';
import type React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUserName, signOutUser } from 'redux/slices/user.slice';
import { useLogoutUserMutation, useUpdateCurrentUserMutation } from 'services/api/user.api';
import { NAVY, YELLOW } from 'styles/tokens';
import { posthogReset } from 'utils/posthogUtils';

import ChangePassword from './ChangePassword';
import NotificationsPermissions from './NotificationsPermissions';

const Profile = () => {
  const currentUser = useCurrentUser();
  const isAdmin = useIsAdmin();
  const [name, setName] = useState(currentUser?.name || '');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [updateCurrentUser, { isLoading }] = useUpdateCurrentUserMutation();
  const [logoutUser] = useLogoutUserMutation();

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateCurrentUser({ name }).catch((error) => {
      console.error(error);
    });
    dispatch(setUserName(name));
  };

  const initials = (currentUser?.name || currentUser?.email || '?')
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Container maxWidth="md" sx={{ pt: 3, pb: 6 }}>
      <PageTitle sx={{ mb: 4 }}>Mi Perfil</PageTitle>

      {/* ── Información Personal ───────────────────────────────────────── */}
      <FormSection
        title="Información personal"
        description="Actualiza tu nombre visible. El email no se puede modificar."
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: YELLOW,
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
            }}
          >
            {initials || <PersonIcon sx={{ fontSize: 32 }} />}
          </Avatar>
          <Box>
            <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY, fontSize: '1rem' }}>
              {currentUser?.name || 'Sin nombre'}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.6) }}>
              {currentUser?.email}
            </Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleNameSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormField label="Email" value={currentUser?.email ?? ''} disabled />
          <FormField
            label="Nombre"
            value={name}
            onChange={(event) => setName(event.target.value)}
            hint="Tu nombre será visible para otros usuarios"
            required
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <PrimaryButton type="submit" disabled={!name || isLoading}>
              {isLoading ? 'Actualizando…' : 'Guardar cambios'}
            </PrimaryButton>
          </Box>
        </Box>
      </FormSection>

      {/* ── Notificaciones ─────────────────────────────────────────────── */}
      <FormSection title="Notificaciones" description="Configura cómo recibes alertas de la plataforma.">
        <NotificationsPermissions />
      </FormSection>

      {/* ── Seguridad ──────────────────────────────────────────────────── */}
      <FormSection title="Seguridad" description="Cambia tu contraseña periódicamente para mantener tu cuenta segura.">
        <ChangePassword />
      </FormSection>

      {/* ── Integraciones ──────────────────────────────────────────────── */}
      {isAdmin && (
        <FormSection
          title="Integraciones Google"
          description="Conecta Gmail, Google Meet y otros servicios a tu cuenta."
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <SecondaryButton onClick={() => navigate('/google-integrations')}>Configurar</SecondaryButton>
          </Box>
        </FormSection>
      )}

      {/* ── Cerrar sesión ──────────────────────────────────────────────── */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <BackButton
          onClick={() => {
            posthogReset();
            // Fire-and-forget server-side revocation of the refresh-token family.
            logoutUser();
            dispatch(signOutUser());
            navigate('/');
          }}
          startIcon={<LogoutIcon />}
          sx={{ color: '#E7233B', '&:hover': { bgcolor: alpha('#E7233B', 0.06) } }}
        >
          Cerrar sesión
        </BackButton>
      </Box>
    </Container>
  );
};

export default Profile;
